import { Link, useNavigate, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { Header } from "../components/Header";
import { Breadcrumbs, PublicFooter } from "../components/PublicChrome";
import { ShieldPreview } from "../components/shield/ShieldPreview";
import { useSeo, breadcrumbs, ORGANIZATION, SITE_URL } from "../lib/seo";
import { REGION_FACTS } from "../lib/regionFacts";
import { SIZES, eur, shieldMm } from "../lib/shield";
import { pageFor, pagePath, regionDesign, GAA_PAGES, RUGBY_PAGES } from "../lib/regionPages";
import NotFoundCounty from "./Counties";

const copyFor = (p) => {
  const gaa = p.sport === "gaa";
  const what = gaa ? `${p.name} GAA` : `${p.name} rugby`;
  return {
    what,
    title: `${p.name} ${gaa ? "GAA" : "Rugby"} Player Cards & Shields | sportsshields.ie`,
    h1: `${what} player cards & shields`,
    desc: `Personalised ${what} player cards & shields in ${p.colourText} – photo, name, stats and the ${p.name} badge. Printed in Ireland from ${eur(SIZES[0].price)}.`,
    fact: REGION_FACTS[!gaa && p.id === "lei" ? "lei-r" : p.id],
    intro: gaa
      ? `From the first nursery session to minor level, there's nothing like pulling on the ${p.colourText} of ${p.nick === "Overseas GAA" ? p.name : p.nick}. Turn your young footballer, hurler or camogie player into a ${p.name} legend with a personalised player shield: their photo, name, position, rating and six stats, finished with the ${p.code} county badge.`
      : `Whether they're scrumming down at minis or kicking for the posts at U14s, give your young ${p.name} fan a shield in ${p.colourText} with the ${p.name} provincial flag – complete with their photo, name, position, rating and stats like tackling, handling and kicking.`,
    faq: [
      [`Can I get the ${p.name} badge on the shield?`, `Yes. Choose ${gaa ? "County" : "Province"} in the flag section and pick ${p.name} – the "Design yours" button below sets it up for you.`],
      [`Can I use my own club colours instead of ${p.colourText}?`, "Of course. Pick any palette or exact club colours, add your club crest and keep the badge."],
      [gaa ? "Does it work for hurling and camogie too?" : "Which positions can I choose?", gaa ? "Yes – choose Gaelic football or Hurling / Camogie and the stats change to match (stick skill for hurling)." : "Every rugby position from prop to full back, or type your own."],
      ["When will it arrive?", "Every shield is printed in Ireland and delivered in 7–10 working days."],
    ],
  };
};

export default function RegionPage({ sport }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const p = pageFor(sport, slug);
  const c = p && copyFor(p);
  const crumbs = p ? [["Home", "/"], ["Counties & provinces", "/counties"], [c.h1, pagePath(p)]] : [];
  useSeo(p ? { title: c.title, description: c.desc, path: pagePath(p), jsonLd: { "@context": "https://schema.org", "@graph": [ORGANIZATION, breadcrumbs(crumbs),
    { "@type": "FAQPage", mainEntity: c.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    { "@type": "Product", name: c.h1, description: c.desc, brand: { "@type": "Brand", name: "sportsshields.ie" },
      offers: { "@type": "AggregateOffer", lowPrice: (SIZES[0].price / 100).toFixed(2), highPrice: (SIZES[3].price / 100).toFixed(2), priceCurrency: "EUR", offerCount: SIZES.length, url: `${SITE_URL}${pagePath(p)}` } }] } }
    : { title: "Page not found | sportsshields.ie", description: "Page not found.", noindex: true });
  if (!p) return <NotFoundCounty missing />;
  const others = (sport === "rugby" ? RUGBY_PAGES : GAA_PAGES).filter((o) => o.id !== p.id).slice(0, 40);
  return (
    <div className="min-h-screen">
      <Header onNew={() => nav("/design")} />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-12">
        <Breadcrumbs items={crumbs} />
        <section className="mt-4 grid items-center gap-10 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <div className="eyebrow">{p.nick} · {p.code}</div>
            <h1 className="font-display mt-2 text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl" data-testid="region-title">{c.h1}</h1>
            <p className="mt-5 max-w-xl text-slate-600" data-testid="region-intro">{c.intro}</p>
            {c.fact && <p className="mt-3 max-w-xl text-slate-600" data-testid="region-fact">{c.fact}</p>}
            <ul className="mt-5 space-y-2 text-sm">
              {[`${p.name} ${sport === "rugby" ? "provincial flag" : "county badge"} in ${p.colourText}`, "Photo with free background removal", "Name, position, rating and six stats", "Printed in Ireland · 7–10 working days"].map((t) => (
                <li key={t} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-lime-600" />{t}</li>
              ))}
            </ul>
            <Link to={`/design?region=${sport}/${p.slug}`} className="btn-blaze mt-7 inline-block px-6 py-3 text-sm" data-testid="region-design-button">Design yours in {p.name} colours</Link>
          </div>
          <div className="mx-auto w-full max-w-[300px] [filter:drop-shadow(0_18px_24px_rgba(15,23,42,.35))]" data-testid="region-sample-shield">
            <ShieldPreview design={regionDesign(p, { sample: true })} testId="region-shield-svg" />
          </div>
        </section>
        <section className="mt-14" aria-labelledby="region-sizes-h">
          <h2 id="region-sizes-h" className="font-display text-2xl font-black uppercase">Sizes & prices</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {SIZES.map((s) => (
              <div key={s.id} className="border-2 border-slate-200 bg-white p-4" data-testid={`region-size-${s.id}`}>
                <div className="font-display font-extrabold uppercase">{s.name}</div>
                <div className="font-mono-cc text-xs text-slate-500">Shield {Math.round(shieldMm(s).w / 10)} × {Math.round(s.h / 10)} cm</div>
                <div className="font-cond mt-2 text-2xl font-bold">{eur(s.price)}</div>
              </div>
            ))}
          </div>
          <Link to="/shields#size-chart" className="mt-3 inline-block text-sm font-bold underline">Compare sizes to scale</Link>
        </section>
        <section className="mt-14 max-w-3xl" aria-labelledby="region-faq-h">
          <h2 id="region-faq-h" className="font-display text-2xl font-black uppercase">{p.name} shield FAQ</h2>
          <dl className="mt-4 divide-y border-y-2 border-slate-200">
            {c.faq.map(([q, a]) => <div key={q} className="py-3"><dt className="font-bold">{q}</dt><dd className="mt-1 text-sm text-slate-600">{a}</dd></div>)}
          </dl>
        </section>
        <nav className="mt-14" aria-label={sport === "rugby" ? "Other provinces" : "Other counties"}>
          <h2 className="font-display text-lg font-extrabold uppercase">{sport === "rugby" ? "Other provinces" : "Other counties"}</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {others.map((o) => <Link key={o.id} to={pagePath(o)} className="border-2 border-slate-200 bg-white px-3 py-1.5 text-xs font-bold hover:border-slate-900">{o.name}</Link>)}
          </div>
        </nav>
      </main>
      <PublicFooter />
    </div>
  );
}
